import { Button } from "@jti/core-components/components/Button";
import { formatPrice } from "@jti/format-price";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ProductDetailsCtaLayout } from "@product-details/components/ProductDetailsCtaLayout";
import { ProductDetailsCtaMobileProductData } from "@product-details/components/ProductDetailsCtaMobileProductData";
import { getFormattedPriceForQuantity } from "@product-details/utils/getFormattedPriceForQuantity";
import { handleError } from "@product-details/utils/handleError";

import styles from "./ConsumablesFormSubmitButton.module.scss";

type ConsumablesFormSubmitButtonProps = {
  productName: string;
  finalPrices: { value: number; currency: string; multiplier: number }[];
  discountedPrice?: { value: number; currency: string };
  quantity: number;
  isProductDataLoading: boolean;
  isAddProductInProgress: boolean;
  isOutOfStockProductSelected: boolean;
  isProductDataLoaded: boolean;
  isCartLoaded: boolean;
  showPricePerMonthLabel: boolean;
  customBuyCtaLabel?: string;
};

export const ConsumablesFormSubmitButton = ({
  productName,
  finalPrices,
  discountedPrice,
  quantity,
  isProductDataLoading,
  isAddProductInProgress,
  isProductDataLoaded,
  isOutOfStockProductSelected,
  isCartLoaded,
  showPricePerMonthLabel,
  customBuyCtaLabel,
}: ConsumablesFormSubmitButtonProps) => {
  const { __ } = useTranslations();

  let calculatedFinalPrice;
  const baseLabel = customBuyCtaLabel || __("com.product.add_to_cart_button_label");

  const canCalculateFinalPrices = !isProductDataLoading && isProductDataLoaded;

  if (canCalculateFinalPrices) {
    calculatedFinalPrice = discountedPrice
      ? formatPrice(discountedPrice)
      : getFormattedPriceForQuantity(finalPrices, quantity);
  }

  if (canCalculateFinalPrices && !calculatedFinalPrice) {
    handleError(`Cannot calculate final price for quantity: ${quantity}`);
  }

  const labelWithPrice = calculatedFinalPrice
    ? `${baseLabel}(${calculatedFinalPrice}${showPricePerMonthLabel ? "/" + __("product.info.monthlyPriceLabel") : ""})`
    : baseLabel;

  const buttonProps = {
    type: "submit" as const,
    pending: isAddProductInProgress,
    disabled: isAddProductInProgress || isOutOfStockProductSelected || !isCartLoaded,
    "data-testid": "sticks-form-submit-button",
  };

  return (
    <ProductDetailsCtaLayout
      mobileCtaContent={
        <ProductDetailsCtaMobileProductData
          productName={productName}
          productPrice={calculatedFinalPrice}
          showPricePerMonthLabel={showPricePerMonthLabel}
        />
      }
    >
      <>
        <span className={styles.buttonWithoutPriceWrapper}>
          <Button {...buttonProps}>{baseLabel}</Button>
        </span>
        <span className={styles.buttonWithPriceWrapper}>
          <Button {...buttonProps}>{labelWithPrice}</Button>
        </span>
      </>
    </ProductDetailsCtaLayout>
  );
};
